import { ClipboardCheck, Filter } from "lucide-react";
import { useEffect, useState } from "react";
import LessonSelect from "../selectors/LessonSelect";
import DepartmentTreeSelect from "../selectors/DepartmentTreeSelect";
import { ApiService } from "../../../../services/api";

export type TestReportStatusFilter = "all" | "COMPLETED" | "FAILED" | "PENDING";

interface TestHorizontalFilterProps {
  selectedClass: string | null;
  onClassChange: (classId: string | null) => void;

  selectedSubClass: string[];
  onSubClassChange: (subClassIds: string[]) => void;

  selectedLesson: string | null;
  onLessonChange: (lessonId: string | null) => void;

  selectedTopic: string | null;
  onTopicChange: (topicId: string | null) => void;

  selectedTest: string[];
  onTestChange: (testIds: string[]) => void;

  selectedStatus: TestReportStatusFilter;
  onStatusChange: (status: TestReportStatusFilter) => void;

  startDate: string | null;
  endDate: string | null;
  onDateChange: (startDate: string | null, endDate: string | null) => void;

  onFilter: () => void;
  onParentDepartmentChange?: (deptId: string | null) => void;
}

export default function TestHorizontalFilter({
  selectedClass,
  onClassChange,
  selectedSubClass,
  onSubClassChange,
  selectedLesson,
  onLessonChange,
  selectedTopic,
  onTopicChange,
  selectedTest,
  onTestChange,
  selectedStatus,
  onStatusChange,
  startDate,
  endDate,
  onDateChange,
  onFilter,
  onParentDepartmentChange,
}: TestHorizontalFilterProps) {
  const [parentDepartmentId, setParentDepartmentId] = useState<string | null>(null);
  const [sections, setSections] = useState<Array<{ id: string; title: string }>>([]);
  const [tests, setTests] = useState<Array<{ id: string; title: string }>>([]);

  const isFilterReady = () => {
    return selectedClass && selectedSubClass && selectedSubClass.length > 0 && selectedTopic && selectedTest && selectedTest.length > 0;
  };

  const buildMissingMessage = () => {
    const parts: string[] = [];
    if (!selectedClass) parts.push("Ders");
    if (!(selectedSubClass && selectedSubClass.length > 0)) parts.push("Departman");
    if (!selectedTopic) parts.push("Bölüm");
    if (!(selectedTest && selectedTest.length > 0)) parts.push("Test");
    if (parts.length === 0) return "";
    if (parts.length === 1) return `${parts[0]} seçiniz.`;
    if (parts.length === 2) return `${parts[0]} ve ${parts[1]} seçiniz.`;
    return `${parts.slice(0, -1).join(", ")} ve ${parts[parts.length - 1]} seçiniz.`;
  };

  useEffect(() => {
    const loadSections = async () => {
      if (!selectedLesson) {
        setSections([]);
        return;
      }
      try {
        const res: any = await ApiService.getLessonSections(selectedLesson);
        const list = (res?.data || res || []).map((s: any) => ({
          id: String(s.id || s._id || s.sectionId || ""),
          title: String(s.title || s.name || "Bölüm"),
        }));
        setSections(list);
      } catch (_) {
        setSections([]);
      }
    };
    loadSections();
  }, [selectedLesson]);

  useEffect(() => {
    const loadTests = async () => {
      if (!selectedLesson || !selectedTopic) {
        setTests([]);
        return;
      }
      try {
        const res: any = await ApiService.getLessonSectionTests(selectedLesson, selectedTopic);
        const list = (res?.data || res || []).map((t: any) => ({
          id: String(t.id || t._id || t.contentId || ""),
          title: String(t.title || t.name || "Test"),
        }));
        setTests(list);
      } catch (_) {
        setTests([]);
      }
    };
    loadTests();
  }, [selectedLesson, selectedTopic]);

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-6">
      <div className="flex items-center gap-3 mb-4">
        <Filter className="w-5 h-5 text-gray-600" />
        <h3 className="text-lg font-semibold text-gray-900">Filtreler</h3>
      </div>

      <div className="max-h-96 overflow-y-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 gap-4 mb-6">
          <div className="col-span-1">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">Ders</label>
            <LessonSelect
              value={selectedClass}
              onChange={(v) => {
                onClassChange(v);
                onLessonChange(v);
                setParentDepartmentId(null);
                if (onParentDepartmentChange) onParentDepartmentChange(null);
                setSections([]);
                setTests([]);
                onTopicChange(null);
                onTestChange([]);
              }}
            />
          </div>

          <div className="col-span-1">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">Departman</label>
            <DepartmentTreeSelect value={selectedSubClass || []} onChange={onSubClassChange} lessonId={selectedLesson} />
          </div>

          <div className="col-span-1">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">Bölüm</label>
            <div>
              <div className="border border-gray-300 rounded-lg overflow-hidden">
                <div className="max-h-64 overflow-auto divide-y divide-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      onTopicChange(null);
                      setTests([]);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                      selectedTopic ? "hover:bg-gray-50 text-gray-800" : "bg-blue-50 text-blue-900"
                    }`}
                    disabled={!selectedLesson}>
                    Bölüm Seçiniz
                  </button>
                  {sections.map((sec) => {
                    const selected = sec.id === selectedTopic;
                    return (
                      <button
                        key={sec.id}
                        type="button"
                        onClick={() => onTopicChange(sec.id)}
                        className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                          selected ? "bg-blue-50 text-blue-900" : "hover:bg-gray-50 text-gray-800"
                        }`}
                        disabled={!selectedLesson}>
                        {sec.title}
                      </button>
                    );
                  })}
                  {selectedLesson && sections.length === 0 && <div className="px-3 py-2 text-sm text-gray-500">Bölüm yok.</div>}
                </div>
              </div>
            </div>
          </div>

          <div className="col-span-1">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">Test</label>
            <div>
              <div className="border border-gray-300 rounded-lg overflow-hidden">
                <div className="max-h-64 overflow-auto divide-y divide-gray-200">
                  <button
                    type="button"
                    onClick={() => onTestChange([])}
                    className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                      selectedTest && selectedTest.length > 0 ? "hover:bg-gray-50 text-gray-800" : "bg-blue-50 text-blue-900"
                    }`}
                    disabled={!selectedTopic}>
                    Test Seçiniz
                  </button>
                  {tests.map((t) => {
                    const selected = (selectedTest || []).includes(t.id);
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => {
                          const setIds = new Set(selectedTest || []);
                          if (setIds.has(t.id)) setIds.delete(t.id);
                          else setIds.add(t.id);
                          onTestChange(Array.from(setIds));
                        }}
                        className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                          selected ? "bg-blue-50 text-blue-900" : "hover:bg-gray-50 text-gray-800"
                        }`}
                        disabled={!selectedTopic}>
                        {t.title}
                      </button>
                    );
                  })}
                  {selectedTopic && tests.length === 0 && <div className="px-3 py-2 text-sm text-gray-500">Test yok.</div>}
                </div>
              </div>
            </div>
          </div>

          <div className="col-span-1 flex items-end">
            <button
              onClick={onFilter}
              disabled={!isFilterReady()}
              className="px-6 py-2.5 rounded-lg font-medium text-sm transition-colors bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed">
              Filtrele
            </button>
            <span className="text-xs sm:text-sm text-gray-600 ml-2">
              {!isFilterReady() && `Test raporlarını görüntülemek için lütfen ${buildMissingMessage()}`}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
